import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../data/supabaseClient';
import { clearConversationsCache } from './ConversationsList';
import { chatOpenState } from './chatOpenState';

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [lastConversationId, setLastConversationId] = useState(null);
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [unreadConversations, setUnreadConversations] = useState(new Set());
  const [showConversationList, setShowConversationList] = useState(true);
  const [otherUnreadMessages, setOtherUnreadMessages] = useState(new Set());
  const [messageSubscriptions, setMessageSubscriptions] = useState(new Map());
  // Store occurrence context for recurring posts
  const [occurrenceContext, setOccurrenceContext] = useState(null); // { occurrenceId, occurrenceDate }

  // Mirror open-chat state into a shared module so the notifications toast path
  // (a separate provider tree) can suppress toasts for the conversation in view.
  useEffect(() => {
    chatOpenState.isChatOpen = isChatOpen;
    chatOpenState.activeConversationId = activeConversationId;
  }, [isChatOpen, activeConversationId]);

  // Native only: report which conversation is being viewed so the push backend
  // suppresses message pushes while the user is already looking at that chat.
  // A heartbeat keeps the "viewing" window fresh; it goes stale (and pushes
  // resume) shortly after the chat is closed or the app is backgrounded.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    let intervalId;

    const writePresence = async (convId) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        await supabase
          .from('profiles')
          .update({
            active_conversation_id: convId,
            active_conversation_at: convId ? new Date().toISOString() : null,
          })
          .eq('id', user.id);
      } catch (_) { /* presence is best-effort */ }
    };

    if (isChatOpen && activeConversationId) {
      writePresence(activeConversationId);
      intervalId = setInterval(() => writePresence(activeConversationId), 25000);
    } else {
      writePresence(null);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [isChatOpen, activeConversationId]);

  // Function to load unread conversations from database
  const loadUnreadConversations = useCallback(async (userId) => {
    try {

      // NEW APPROACH: Query message_read_status table for unread messages
      const { data: unreadStatuses, error } = await supabase
        .from('message_read_status')
        .select(`
          message_id,
          messages!inner(conversation_id)
        `)
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('Error fetching unread messages:', error);
        return;
      }

      if (unreadStatuses && unreadStatuses.length > 0) {
        // Extract unique conversation IDs
        const unreadConvIds = [...new Set(
          unreadStatuses.map(s => s.messages.conversation_id)
        )];

        // Update the unread messages state
        setOtherUnreadMessages(new Set(unreadConvIds));
      } else {
        setOtherUnreadMessages(new Set());
      }
    } catch (error) {
      console.error('Error loading unread conversations:', error);
    }
  }, []);

  // Function to mark messages as delivered when user comes online
  const markMessagesAsDelivered = useCallback(async (userId) => {
    try {

      // Mark all undelivered messages as delivered for this user
      const { error } = await supabase
        .from('message_read_status')
        .update({
          is_delivered: true,
          delivered_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('is_delivered', false);

      if (error) {
        console.error('❌ Error marking messages as delivered:', error);
        return;
      }

    } catch (error) {
      console.error('❌ Error in markMessagesAsDelivered:', error);
    }
  }, []);

  // Listen to auth state changes and reset chat state on logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {

      if (event === 'SIGNED_OUT') {
        // Reset all chat state when user logs out
        setIsChatOpen(false);
        setActiveConversationId(null);
        setLastConversationId(null);
        setNotificationQueue([]);
        setUnreadConversations(new Set());
        setShowConversationList(true);
        setOtherUnreadMessages(new Set());
        setMessageSubscriptions(new Map());
        setOccurrenceContext(null);
        clearConversationsCache(); // don't flash the prev user's list on shared-device re-login
      } else if (event === 'SIGNED_IN' && session?.user) {
        // Load unread conversations when user signs in (non-blocking)
        loadUnreadConversations(session.user.id).catch(err => {
          console.error('Failed to load unread conversations:', err);
        });
        // Mark messages as delivered when user comes online
        markMessagesAsDelivered(session.user.id).catch(err => {
          console.error('Failed to mark messages as delivered:', err);
        });
      } else if (event === 'INITIAL_SESSION' && session?.user) {
        // Also load on initial session (page refresh with existing session) (non-blocking)
        loadUnreadConversations(session.user.id).catch(err => {
          console.error('Failed to load unread conversations:', err);
        });
        // Mark messages as delivered on initial session too
        markMessagesAsDelivered(session.user.id).catch(err => {
          console.error('Failed to mark messages as delivered:', err);
        });
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [loadUnreadConversations, markMessagesAsDelivered]);

  const addNotification = useCallback((notification) => {
    const { conversationId, sender, timestamp } = notification;
  
    if (isChatOpen && activeConversationId === conversationId) {
      return;
    }
  
    setUnreadConversations((prev) => new Set([...prev, conversationId]));
  
    if (!isChatOpen) {
      setNotificationQueue((prev) => {
        const newQueue = [...prev, { 
          id: Date.now(), 
          conversationId, 
          sender, 
          timestamp 
        }];
        return newQueue.slice(-3);
      });
  
      setTimeout(() => {
        setNotificationQueue((prev) => 
          prev.filter((n) => n.id !== notification.id)
        );
      }, 3000);
    }
  }, [isChatOpen, activeConversationId]);

  const addOtherUnreadMessage = useCallback((conversationId) => {
    
    if (conversationId !== activeConversationId) {
      setOtherUnreadMessages(prev => {
        const newSet = new Set(prev);
        newSet.add(conversationId);
        return newSet;
      });
    }
  }, [activeConversationId]);

  const clearOtherUnreadMessage = useCallback((conversationId) => {
    setOtherUnreadMessages(prev => {
      const newSet = new Set(prev);
      newSet.delete(conversationId);
      return newSet;
    });
  }, []);
  

  const markConversationAsRead = useCallback((conversationId) => {
    setUnreadConversations((prev) => {
      const updated = new Set(prev);
      updated.delete(conversationId);
      return updated;
    });
  }, []);

  // Function to check if user can start a new conversation based on their limits
  const checkCanStartChat = useCallback(async () => {
    try {
      // First check if we have a valid user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No authenticated user');
        return { canChat: false, limitData: null, error: 'Not authenticated' };
      }
      
      // Use the get_user_chat_usage function directly
      const { data, error } = await supabase.rpc('get_user_chat_usage', {
        p_user_id: user.id
      });
        
      if (error) {
        console.error('Error checking chat limits:', error);
        return { canChat: true, limitData: null, error };
      }
      
      // If no data returned, default to allowing chats
      if (!data) {
        return { 
          canChat: true, 
          limitData: {
            plan_type: 'free',
            remaining: 5,
            max_allowed: 5,
            has_remaining: true,
            monthly_count: 0,
            last_reset: new Date().toISOString()
          },
          error: null
        };
      }
      
      const limitData = {
        plan_type: data.plan_type || 'free',
        remaining: data.remaining_chats || 0,
        max_allowed: data.monthly_limit || 5,
        has_remaining: (data.remaining_chats > 0) || (data.monthly_limit >= 9999),
        monthly_count: data.monthly_count || 0,
        last_reset: data.last_reset || new Date().toISOString()
      };
      
      return { 
        canChat: limitData.has_remaining, 
        limitData,
        error: null
      };
    } catch (error) {
      console.error('Error checking chat limits:', error);
      return { canChat: true, limitData: null, error };
    }
  }, []);
  
  const openChatWithConversation = useCallback((conversationId, occurrenceData = null) => {
    setIsChatOpen(true);
    setActiveConversationId(conversationId);
    setShowConversationList(false); // This will make it go directly to chat

    // Store occurrence context if provided (for recurring posts)
    if (occurrenceData) {
      setOccurrenceContext(occurrenceData);
    } else {
      setOccurrenceContext(null);
    }

    if (conversationId) {
      markConversationAsRead(conversationId);
    }
  }, [markConversationAsRead]);

  // Function to open chat with a specific user (for notifications)
  const openChat = useCallback(async (conversationId, otherUserId) => {
    
    if (conversationId) {
      // If we have a conversation ID, open it directly
      openChatWithConversation(conversationId);
    } else if (otherUserId) {
      // If we only have a user ID, find or create a conversation
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error('No authenticated user');
          return;
        }

        // First, try to find an existing conversation between these two users
        const { data: existingConversations, error: searchError } = await supabase
          .from('conversation_participants')
          .select(`
            conversation_id,
            conversations!inner(
              id,
              participant_ids
            )
          `)
          .eq('user_id', user.id)
          .eq('conversations.participant_ids', otherUserId);

        if (searchError) {
          console.error('Error searching for existing conversation:', searchError);
        }

        if (existingConversations && existingConversations.length > 0) {
          // Found existing conversation, open it
          const conversationId = existingConversations[0].conversation_id;
          openChatWithConversation(conversationId);
        } else {
          // No existing conversation, we'll need to create one
          // For now, just open the chat and let the user start a new conversation
          setIsChatOpen(true);
          setShowConversationList(true);
        }
      } catch (error) {
        console.error('Error opening chat:', error);
        // Fallback: just open the chat interface
        setIsChatOpen(true);
        setShowConversationList(true);
      }
    } else {
      // No conversation ID or user ID, just open the chat interface
      setIsChatOpen(true);
      setShowConversationList(true);
    }
  }, [openChatWithConversation]);

  const showConversations = useCallback(() => {
    setShowConversationList(true);
    setActiveConversationId(null);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setActiveConversationId(null);
    setShowConversationList(true); // Reset to show list when reopening
  }, []);

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => {
      const next = !prev;
      // Opening via the toggle button always starts at the conversation list —
      // specific opens use openChatWithConversation / openChat instead. Without
      // this reset, reopening jumps back into the last conversation you viewed.
      if (next) {
        setActiveConversationId(null);
        setShowConversationList(true);
      }
      return next;
    });
  }, []);
  

  return (
    <ChatContext.Provider
      value={{
        isChatOpen,
        activeConversationId,
        showConversationList,
        notificationQueue,
        unreadConversations,
        openChatWithConversation,
        openChat, // Add the new openChat function
        closeChat,
        toggleChat,
        addNotification,
        markConversationAsRead,
        showConversations,
        otherUnreadMessages,
        addOtherUnreadMessage,
        clearOtherUnreadMessage,
        messageSubscriptions,
        setMessageSubscriptions,
        checkCanStartChat, // Add the new function to the context
        occurrenceContext, // Expose occurrence context for recurring posts
        setOccurrenceContext, // Allow clearing/setting occurrence context
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};